import type { ShearConnectionPrioritizationDTO } from '@api/fastAPI.schemas'
import { SHEAR_CONNECTION_CATEGORIES } from '../constants'

export type ShearPrioritizationCategory = keyof ShearConnectionPrioritizationDTO

/**
 * What to do with one edit to the prioritization lists.
 *
 * `incomplete` keeps the edit on screen but must not be persisted: every
 * storey category needs at least one Schubverbinder, or the shear anchor
 * check would silently produce nothing for the walls in it (the backend
 * rejects it too). Mirrors how the Zugverbinder lists behave — the removal
 * takes effect, only the save is refused, with an error message naming the
 * categories that are now empty.
 */
export type ShearPrioritizationChange =
  | { kind: 'save'; prioritization: ShearConnectionPrioritizationDTO }
  | {
      kind: 'incomplete'
      prioritization: ShearConnectionPrioritizationDTO
      missingLabels: string[]
    }

/** Labels of the categories that currently hold no Schubverbinder, in list order. */
export const emptyShearPrioritizationLabels = (
  prioritization: ShearConnectionPrioritizationDTO,
): string[] =>
  SHEAR_CONNECTION_CATEGORIES.filter(({ id }) => (prioritization[id] ?? []).length === 0).map(
    ({ label }) => label,
  )

export const resolveShearPrioritizationChange = (
  current: ShearConnectionPrioritizationDTO,
  categoryId: ShearPrioritizationCategory,
  next: string[],
): ShearPrioritizationChange => {
  const prioritization: ShearConnectionPrioritizationDTO = { ...current, [categoryId]: next }

  const missingLabels = emptyShearPrioritizationLabels(prioritization)

  if (missingLabels.length > 0) {
    return { kind: 'incomplete', prioritization, missingLabels }
  }

  return { kind: 'save', prioritization }
}

export const shearPrioritizationErrorMessage = (missingLabels: string[]): string =>
  `Bitte wählen Sie für ${missingLabels.join(', ')} mindestens einen Schubverbinder`

/**
 * Message of the banner that stays on screen while a category is empty, worded
 * like the Zugverbinder one so both managers read the same.
 */
export const shearPrioritizationWarningMessage = (missingLabels: string[]): string =>
  `Bitte setzen Sie die Priorisierung für: ${missingLabels.join(', ')}`
